import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { asset } from "@/lib/assets";
const caflerImg = asset("case-studies/UIexamples/Cafler.png");
const caflerDetailImg = asset("case-studies/UIexamples/Caferdetail.png");
const wtwDsImg = asset("case-studies/UIexamples/wtw-ds.webp");
const voxImg = asset("case-studies/UIexamples/Vox.png");
const voxFullImg = asset("case-studies/UIexamples/VoxFull.png");
const cashiQRImg = asset("case-studies/UIexamples/CashiQR.png");
const canela1Img = asset("case-studies/UIexamples/canela1.webp");
const canela2Img = asset("case-studies/UIexamples/canela2.webp");
const googleImg = asset("case-studies/UIexamples/google.webp");

// ─── Media slots ────────────────────────────────────────────────────────────
// Add images/videos here. span: "normal" | "wide" (2 cols) | "full" (3 cols)
// Recommended sizes:
//   normal  → 900 × 675 px  (4:3)  ~200 KB
//   wide    → 1800 × 675 px (8:3)  ~350 KB
//   full    → 1800 × 900 px (16:9) ~400 KB
//   video   → MP4 720p,  poster image same size as slot
type MediaType = "image" | "video";
type SpanType = "normal" | "half" | "wide" | "full";

interface GalleryItem {
  id: number;
  type: MediaType;
  src?: string;
  detailSrc?: string; // shown in lightbox instead of src (e.g. full-length scroll image)
  poster?: string;    // for video only
  alt: string;
  span: SpanType;
  fit?: "cover" | "contain"; // defaults to "cover"
}

const GALLERY_ITEMS: GalleryItem[] = [
  { id: 1,  type: "image", src: caflerImg, detailSrc: caflerDetailImg, alt: "Cafler UI", span: "full" },
  { id: 3,  type: "image", src: voxImg, detailSrc: voxFullImg, alt: "Vox", span: "full" },
  { id: 2,  type: "image", src: wtwDsImg, alt: "WTW design system", span: "half" },
  { id: 4,  type: "image", src: googleImg, alt: "Google One", span: "half" },
  { id: 5,  type: "image", alt: "UI example 5",  span: "normal" },
  { id: 6,  type: "image", src: cashiQRImg, alt: "CashiQR", span: "full", fit: "contain" },
  { id: 8,  type: "image", src: canela1Img, alt: "Canela 1", span: "half" },
  { id: 9,  type: "image", src: canela2Img, alt: "Canela 2", span: "half" },
  { id: 10, type: "image", alt: "UI example 10", span: "wide"   },
  { id: 11, type: "image", alt: "UI example 11", span: "normal" },
];

// ─── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({
  items,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const item = items[index];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Check if content overflows — show hint only when it does
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setShowScrollHint(el.scrollHeight > el.clientHeight + 8);
    // Wait for image to load before measuring
    const img = el.querySelector("img");
    if (img && !img.complete) {
      img.addEventListener("load", check, { once: true });
    } else {
      check();
    }
    return () => { img?.removeEventListener("load", check); };
  }, [index]);

  // Hide when scrolling up or at bottom, show when scrolling down with content remaining
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let lastScrollTop = 0;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 8;
      const scrollingUp = scrollTop < lastScrollTop;
      setShowScrollHint(!atBottom && !scrollingUp);
      lastScrollTop = scrollTop;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 z-[20000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-5 right-5 text-white/70 hover:text-white text-3xl leading-none"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>

      {/* Counter */}
      <span className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-[DM_Sans]">
        {index + 1} / {items.length}
      </span>

      {/* Media — scrollable when detailSrc is a tall image */}
      <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <div
          ref={scrollRef}
          className="w-full max-h-[85vh] overflow-y-auto rounded-xl"
        >
          {item.src ? (
            item.type === "video" ? (
              <video
                src={item.detailSrc ?? item.src}
                poster={item.poster}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-xl"
              />
            ) : (
              <img
                src={item.detailSrc ?? item.src}
                alt={item.alt}
                className="w-full rounded-xl"
              />
            )
          ) : (
            <div className="w-full aspect-video bg-white/10 rounded-xl flex items-center justify-center">
              <span className="text-white/30 font-[DM_Sans] text-sm">No media yet</span>
            </div>
          )}
        </div>

        {/* Scroll hint — only shown when content overflows */}
        <div
          className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 transition-opacity duration-500"
          style={{ opacity: showScrollHint ? 1 : 0 }}
          aria-hidden="true"
        >
          <div className="flex flex-col items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-3 py-2">
            <span className="text-white/80 font-[Bricolage_Grotesque] text-[10px] uppercase tracking-widest leading-none">scroll</span>
            <svg
              width="16" height="16" viewBox="0 0 20 20" fill="none"
              className="animate-bounce text-white"
            >
              <path d="M10 3v14M10 17l-4-4M10 17l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Prev / Next */}
      {index > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl leading-none px-2"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      {index < items.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-4xl leading-none px-2"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Next"
        >
          ›
        </button>
      )}
    </div>,
    document.body
  );
}

// ─── Gallery item card ───────────────────────────────────────────────────────
function GalleryCard({
  item,
  onClick,
}: {
  item: GalleryItem;
  onClick: () => void;
}) {
  const spanClass =
    item.span === "full"
      ? "col-span-1 md:col-span-6"
      : item.span === "wide"
      ? "col-span-1 md:col-span-4"
      : item.span === "half"
      ? "col-span-1 md:col-span-3"
      : "col-span-1 md:col-span-2";

  const aspectClass =
    item.span === "full"
      ? "aspect-video"
      : item.span === "wide"
      ? "aspect-[8/3]"
      : item.span === "half"
      ? "aspect-[4/3]"
      : "aspect-[4/3]";

  return (
    <div
      className={`${spanClass} group cursor-zoom-in`}
      onClick={onClick}
    >
      <div
        className={`relative w-full ${aspectClass} rounded-2xl overflow-hidden bg-[#E8E8F0] border border-[#E2E8F0]
                    transition-all duration-300 ease-out
                    group-hover:-translate-y-1 group-hover:shadow-[0_12px_32px_rgba(0,0,0,0.15)]`}
      >
        {item.src ? (
          item.type === "video" ? (
            <video
              src={item.src}
              poster={item.poster}
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={item.src}
              alt={item.alt}
              className={`w-full h-full ${item.fit === "contain" ? "object-contain" : "object-cover"}`}
            />
          )
        ) : (
          /* Placeholder */
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            {item.type === "video" ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="opacity-25">
                <rect x="2" y="4" width="20" height="16" rx="3" stroke="#1A1A1A" strokeWidth="1.5"/>
                <polygon points="10,9 10,15 16,12" fill="#1A1A1A" opacity="0.5"/>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="opacity-25">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="#1A1A1A" strokeWidth="1.5"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#1A1A1A" opacity="0.5"/>
                <path d="M3 15l5-5 4 4 3-3 6 6" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            )}
            <span className="font-[DM_Sans] text-xs text-[#888] uppercase tracking-widest">
              {item.type === "video" ? "Video" : "Image"} {item.id}
            </span>
          </div>
        )}

        {/* Video badge */}
        {item.type === "video" && item.src && (
          <div className="absolute top-3 left-3 bg-black/50 text-white text-[10px] font-[DM_Sans] uppercase tracking-wider px-2 py-1 rounded-full">
            Video
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export function CaseStudyUIExamples() {
  const visibleItems = GALLERY_ITEMS.filter((item) => item.src);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((i: number) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevItem = useCallback(() =>
    setLightboxIndex((i) => (i !== null && i > 0 ? i - 1 : i)), []);
  const nextItem = useCallback(() =>
    setLightboxIndex((i) => (i !== null && i < visibleItems.length - 1 ? i + 1 : i)), [visibleItems.length]);

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#F5F5F7]/80 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1A] transition-colors font-[DM_Sans] text-sm no-underline"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </Link>
          <span className="font-[Bricolage_Grotesque] font-medium text-[#1A1A1A] text-sm">
            UI examples
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <p className="font-[DM_Sans] text-xs uppercase tracking-widest text-[#888] mb-3">
            Mobile · Web · Style guides · Visual Design
          </p>
          <h1 className="font-[Bricolage_Grotesque] font-medium text-[clamp(2rem,5vw,3.5rem)] tracking-[-0.03em] text-[#1A1A1A] mb-4">
            UI examples
          </h1>
          <p className="font-[DM_Sans] text-[#888] text-lg max-w-xl leading-relaxed">
            A selection of interfaces I've designed across products, platforms, and years.
          </p>
        </div>

        {/* Gallery grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {visibleItems.map((item, i) => (
            <GalleryCard
              key={item.id}
              item={item}
              onClick={() => openLightbox(i)}
            />
          ))}
        </div>
      </main>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          items={visibleItems}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevItem}
          onNext={nextItem}
        />
      )}
    </div>
  );
}
